import { useState, useRef, useEffect } from 'react'
import { COUNTRY_CODE_LIST } from '../utils/currencies'

const BLUE   = '#1877F2'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const BORDER = '#E4E6EB'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'

function PhoneInput({
  countryCode = '+880',
  phone = '',
  onCountryCodeChange,
  onPhoneChange,
  placeholder = '1712345678',
  required = false,
  label = 'Phone number',
}) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef()

  const selected = COUNTRY_CODE_LIST.find(c => c.code === countryCode) || COUNTRY_CODE_LIST[0]
  const isBD = countryCode === '+880'

  const filtered = search.trim()
    ? COUNTRY_CODE_LIST.filter(c => c.searchText.includes(search.toLowerCase()))
    : COUNTRY_CODE_LIST

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleSelect = (code) => {
    onCountryCodeChange(code)
    setOpen(false)
    setSearch('')
    // Clear phone if switching to/from BD since digit rules differ
    if (code !== countryCode) onPhoneChange('')
  }

  const handlePhoneChange = (val) => {
    // BD: strip non-digits, enforce 10 digits max
    if (isBD) {
      const digits = val.replace(/\D/g, '')
      onPhoneChange(digits.slice(0, 10))
    } else {
      onPhoneChange(val)
    }
  }

  const bdDigitCount = isBD ? phone.replace(/\D/g, '').length : null
  const bdValid = !isBD || bdDigitCount === 10
  const bdColor = isBD
    ? bdDigitCount === 0 ? BORDER
    : bdDigitCount === 10 ? '#22C55E'
    : '#DC2626'
    : BORDER

  const inp = {
    flex: 1, border: '1px solid', borderColor: open ? BLUE : bdColor,
    borderRadius: '9px', padding: '8px 11px', fontSize: '12.5px',
    outline: 'none', background: WHITE, color: TEXT,
    fontFamily: 'Space Grotesk, sans-serif',
  }

  return (
    <div>
      {label && (
        <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>
          {label}{required && <span style={{ color: '#DC2626', marginLeft: '2px' }}>*</span>}
        </label>
      )}
      <div style={{ display: 'flex', gap: '6px', alignItems: 'stretch' }}>

        {/* Country code picker */}
        <div style={{ position: 'relative', flexShrink: 0 }} ref={dropdownRef}>
          <button type="button" onClick={() => setOpen(!open)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              border: '1px solid', borderColor: open ? BLUE : BORDER,
              borderRadius: '9px', padding: '8px 10px',
              background: WHITE, color: TEXT, cursor: 'pointer',
              minWidth: '88px', height: '100%', fontSize: '12px',
            }}>
            <span style={{ fontSize: '15px' }}>{selected.flag}</span>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11.5px', fontWeight: 600 }}>
              {selected.code}
            </span>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={MUTED}
              strokeWidth="2.5" strokeLinecap="round"
              style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.15s', marginLeft: 'auto' }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {open && (
            <div style={{
              position: 'absolute', left: 0, top: 'calc(100% + 4px)',
              background: WHITE, borderRadius: '12px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              border: '1px solid ' + BORDER, zIndex: 999,
              width: '240px', maxHeight: '280px', overflow: 'hidden',
            }}>
              <div style={{ padding: '8px', borderBottom: '1px solid ' + BORDER }}>
                <input type="text" value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search country or code..."
                  autoFocus
                  style={{
                    width: '100%', border: '1px solid ' + BORDER, borderRadius: '8px',
                    padding: '6px 10px', fontSize: '12px', outline: 'none',
                    background: BG, fontFamily: 'Space Grotesk, sans-serif',
                    boxSizing: 'border-box',
                  }}
                  onFocus={e => e.target.style.borderColor = BLUE}
                  onBlur={e => e.target.style.borderColor = BORDER} />
              </div>
              <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                {filtered.length === 0 ? (
                  <div style={{ padding: '16px', textAlign: 'center', fontSize: '12px', color: MUTED }}>
                    No results
                  </div>
                ) : filtered.map(c => (
                  <button key={c.code + c.country} type="button"
                    onClick={() => handleSelect(c.code)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '8px 12px', textAlign: 'left', border: 'none',
                      background: c.code === countryCode ? BG : WHITE,
                      cursor: 'pointer', fontSize: '12px',
                    }}
                    onMouseEnter={e => { if (c.code !== countryCode) e.currentTarget.style.background = BG }}
                    onMouseLeave={e => { if (c.code !== countryCode) e.currentTarget.style.background = WHITE }}>
                    <span style={{ fontSize: '16px', flexShrink: 0 }}>{c.flag}</span>
                    <span style={{ flex: 1, color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.country}
                    </span>
                    <span style={{ color: BLUE, fontFamily: 'JetBrains Mono, monospace', fontSize: '11.5px', fontWeight: 600, flexShrink: 0 }}>
                      {c.code}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Phone input */}
        <div style={{ flex: 1, position: 'relative' }}>
          <input type="tel" value={phone}
            onChange={e => handlePhoneChange(e.target.value)}
            placeholder={isBD ? '1712345678' : placeholder}
            required={required}
            maxLength={isBD ? 10 : undefined}
            style={inp}
            onFocus={e => e.target.style.borderColor = BLUE}
            onBlur={e => e.target.style.borderColor = bdColor} />
          {/* BD digit counter */}
          {isBD && phone.length > 0 && (
            <div style={{
              position: 'absolute', right: '10px', top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '10.5px', fontWeight: 700,
              color: bdValid ? '#22C55E' : '#DC2626',
            }}>
              {bdDigitCount}/10
            </div>
          )}
        </div>
      </div>

      {/* Hint */}
      <div style={{ fontSize: '11px', color: MUTED, marginTop: '4px' }}>
        {isBD
          ? 'Enter 10 digits after +880 — e.g. 1712345678'
          : `Enter number without leading 0 — e.g. ${selected.code} 412345678`}
      </div>
    </div>
  )
}

export default PhoneInput